import * as React from 'react'
import { Popover as PopoverPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

import { OVERLAY_POPUP_Z_CLASS } from './overlay-panel'

/* -------------------------------------------------------------------------
 * shadcn `Popover` primitive, brought in with `npx shadcn@latest add popover`.
 * It backs four seams and nothing else: the `src/antd/Select` seam's shadcn
 * half (`src/antd/Select/Select.shadcn.tsx`), the
 * `src/antd/Menu.shadcn` collapsed-rail flyout (a hover-driven popup anchored
 * on the rail row), the ANCHORED shape of the `Popconfirm` seam
 * (`src/antd/Popconfirm/Popconfirm.shadcn.tsx`; its modal shape uses
 * `./alert-dialog` instead) and the `src/antd/DatePicker` seam's panel
 * (`src/antd/DatePicker/DatePicker.shadcn.tsx`, which anchors on the picker
 * shell and keeps antd's `getPopupContainer` default like the Select does).
 * Nothing reaches it until someone flips the design toggle.
 *
 * Edits to the CLI's output, each of them a decision:
 *
 * 1. `container` ON `PopoverContent`. The CLI's Portal takes no target, so the
 *    panel always lands on `document.body`. The two antd POPUP seams keep
 *    antd's own `getPopupContainer` default instead — inside the control for a
 *    Select, the trigger's parent for a Dropdown — rather than silently
 *    re-homing 280 Selects and 29 Dropdowns; see the container note in
 *    `src/antd/Select/Select.shadcn.tsx`. The Menu flyout takes the default
 *    (`undefined` = body), matching `src/components/ui/tooltip.tsx`'s
 *    reasoning: it must escape the sidebar's `overflow: hidden` rail, exactly
 *    as antd's own submenu popup did.
 * 2. THE STACKING comes from `./overlay-panel`, not a local `z-50`. antd's
 *    Select popup and Dropdown menu both sit at `zIndexPopupBase + 50`; that
 *    one number is now stated once for every overlay seam.
 * 3. The CLI's `w-72 p-4` panel defaults are dropped for `w-fit … p-0`: every
 *    consumer owns its own width and padding (the Select sizes itself to the
 *    trigger, the rail flyout to its rows, the Popconfirm bubble to antd's own
 *    min/max), and a stock width would fight them through tailwind-merge.
 * 4. `PopoverTrigger`/`PopoverHeader`/`PopoverTitle`/`PopoverDescription` are
 *    dropped. Nothing here composes a popover by hand or uses Radix's own
 *    trigger — every consumer anchors with `PopoverAnchor` and drives `open`
 *    itself (the Popconfirm seam through a `virtualRef`, since it must not ask
 *    its call site's child for a ref) — and an unused export is a call site
 *    waiting to happen outside the seams.
 * ------------------------------------------------------------------------- */

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  container,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  /** Portal target. `undefined` = `document.body`, Radix's own default. */
  container?: HTMLElement | null
}) {
  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground w-fit rounded-md border p-0 shadow-md outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          OVERLAY_POPUP_Z_CLASS,
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

export { Popover, PopoverAnchor, PopoverContent }
